import { Injectable } from '@angular/core';
import { ApplicationInsights, ITelemetryItem } from '@microsoft/applicationinsights-web';
import { environment } from 'src/environments/environment';
import { ConfigurationService } from './configuration.service';
import {
  DIM_DESTINATION_URL,
  DIM_NAVIGATION_TYPE,
  DIM_PLUGIN,
  DIM_REASON,
  DIM_ROUTE_PATH,
  DIM_STATUS,
  EVENT_PLUGIN_NAVIGATION,
  EVENT_PLUGIN_VISITED,
} from './logging-constants';

const CLOUD_ROLE_NAME = 'LabPartnerUI';
const SENSITIVE_TELEMETRY_KEYS = new Set(['linkidentifier']);

export function sanitizeTelemetryUrl(url: string | undefined): string | undefined {
  return url?.split(/[?#]/, 1)[0];
}

export function sanitizeTelemetryProperties(
  properties: { [key: string]: any } | undefined,
): { [key: string]: any } | undefined {
  if (!properties) {
    return undefined;
  }

  return Object.fromEntries(
    Object.entries(properties).filter(
      ([key]) => !SENSITIVE_TELEMETRY_KEYS.has(key.toLowerCase()),
    ),
  );
}

@Injectable()
export class LoggingService {
  private appInsights: ApplicationInsights;
  private currentPlugin = '';
  private currentRoutePath = '';
  private authenticatedUserId = '';

  constructor(private configService: ConfigurationService) {
    const config = this.configService.getConfig();
    const insightsKey = config.appInsightsKey;
    const environmentName = config.environmentName ?? 'unknown';

    this.appInsights = new ApplicationInsights({
      config: {
        instrumentationKey: insightsKey,
        enableAutoRouteTracking: false, // option to log all route changes
      },
    });
    this.appInsights.loadAppInsights();
    this.appInsights.addTelemetryInitializer((item: ITelemetryItem) => {
      item.tags = item.tags ?? {};
      item.tags['ai.cloud.role'] = CLOUD_ROLE_NAME;
      item.data = item.data ?? {};
      item.data['dateTimeUtc'] = new Date().toISOString();
      item.data['environment'] = environmentName;
      item.data['appVersion'] = environment.version;
      if (this.authenticatedUserId) {
        item.data['userId'] = this.authenticatedUserId;
      }
      if (this.currentPlugin) {
        item.data[DIM_PLUGIN] = this.currentPlugin;
      }
      if (this.currentRoutePath) {
        item.data[DIM_ROUTE_PATH] = this.currentRoutePath;
      }
    });
  }

  setNavigationContext(plugin: string | undefined, routePath: string | undefined) {
    this.currentPlugin = plugin ?? '';
    this.currentRoutePath = sanitizeTelemetryUrl(routePath) ?? '';
  }

  setUserId(userId: number) {
    this.authenticatedUserId = userId.toString();
    this.appInsights.setAuthenticatedUserContext(this.authenticatedUserId);
  }

  clearUserId() {
    this.authenticatedUserId = '';
    this.appInsights.clearAuthenticatedUserContext();
  }

  logPluginVisited(plugin: string) {
    this.logEvent(EVENT_PLUGIN_VISITED, { [DIM_PLUGIN]: plugin });
  }

  logPluginNavigation(
    plugin: string,
    routePath: string,
    destinationUrl: string,
    status: 'Attempt' | 'Blocked',
    reason?: string,
  ) {
    const properties: { [key: string]: string } = {
      [DIM_PLUGIN]: plugin,
      [DIM_ROUTE_PATH]: routePath,
      [DIM_DESTINATION_URL]: destinationUrl,
      [DIM_NAVIGATION_TYPE]: 'External',
      [DIM_STATUS]: status,
    };

    if (reason) {
      properties[DIM_REASON] = reason;
    }

    this.logEvent(EVENT_PLUGIN_NAVIGATION, properties);
  }

  logPageView(name?: string, url?: string) {
    // option to call manually
    this.appInsights.trackPageView({
      name: name,
      uri: sanitizeTelemetryUrl(url),
    });
  }

  logEvent(name: string, properties?: { [key: string]: any }) {
    this.appInsights.trackEvent({ name: name }, sanitizeTelemetryProperties(properties));
  }

  logMetric(name: string, average: number, properties?: { [key: string]: any }) {
    this.appInsights.trackMetric(
      { name: name, average: average },
      sanitizeTelemetryProperties(properties),
    );
  }

  logException(exception: Error, severityLevel?: number) {
    this.appInsights.trackException({ exception: exception, severityLevel: severityLevel });
  }

  logTrace(message: string, properties?: { [key: string]: any }) {
    this.appInsights.trackTrace({ message: message }, properties);
  }
}
